import type { Duration } from 'date-fns';
import { clamp, differenceInHours, format, intlFormatDistanceWithOptions, sub } from 'date-fns/fp';

const RFC_3339_NANO_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXXXX";
const RFC_3339_NANO_FORMAT_NS = "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXXXX";
const ISO_8601_DAY_FORMAT = 'yyyy-MM-dd';
const THREE_MONTHS_HOURS = 2160;
const ONE_YEAR_HOURS = 8760;
export const DATE_AT_TIME_FORMAT = "LLLL d, yyyy 'at' h:mmaa";

export const formatRFC3339 = format(RFC_3339_NANO_FORMAT);
export const formatISODay = format(ISO_8601_DAY_FORMAT);

/** Fractional seconds, used only for audit log timestamps */
export const formatRFC3339Nano = format(RFC_3339_NANO_FORMAT_NS);

/**
 * {@link relativePastTime} will round to 1min if the value is < 1m ago or in the future
 * @param baseDate should be `Date.now()` if comparing against current time.
 * */
export const relativePastTime = (
  date: Date | number,
  baseDate: Date | number,
  minDuration: Duration = { minutes: 1 }
) => {
  const relativeTime = intlFormatDistanceWithOptions({
    style: 'narrow',
    numeric: 'always',
  });

  const relativeTimeMonths = intlFormatDistanceWithOptions({
    style: 'narrow',
    numeric: 'always',
    unit: 'month',
  });

  // clamp `date` to being < baseDate - minDuration ago
  const baseDateMinusMinDuration = sub(minDuration)(baseDate);
  const clampedDate = clamp({ start: new Date(0), end: baseDateMinusMinDuration })(date);

  // Force months if we'd otherwise use quarters, which we want to avoid
  const hours = differenceInHours(date, baseDate);
  const forceMonths = hours >= THREE_MONTHS_HOURS && hours <= ONE_YEAR_HOURS;

  // we reverse the arguments here to get "min ago" instead of future time.
  return forceMonths ? relativeTimeMonths(baseDate, clampedDate) : relativeTime(baseDate, clampedDate);
};

/**
 * The instant spelled out in the viewer's locale and zone — "Thursday, August 27, 2027 at 3:58 PM CDT".
 * One module-level formatter: constructing an Intl.DateTimeFormat is the expensive part, formatting is cheap.
 */
const longDateTime = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

export const formatLongDateTime = (date: Date | number) => longDateTime.format(date);
